<template>
    <div v-if="inline" class="revenue-table card-body" data-test="revenue-goals-inline">
        <div class="card-header">
            Online Revenue Goals
        </div>
        <div id="revenueGoals">
            <table class="bg-white table table-striped table-bordered">
                <tr data-test="revenue-goal-row">
                    <th><label for="seasonGoal">Online Revenue Goal</label></th>
                    <td>
                        <input
                            type="text"
                            id="seasonGoal"
                            v-model="revenueGoal"
                            @input="updateRevenueGoal"
                            data-test="revenue-goal-input"
                        />
                    </td>
                </tr>
                <tr data-test="percentage-to-goal-row">
                    <th>
                        <label for="percentageToGoal">Percentage to Online Goal</label>
                    </th>
                    <td class="percent-cell">
                        <div class="progress" style="height: 100%;">
                            <div
                                role="progressbar"
                                class="progress-bar"
                                :class="{'dark': percentageToGoal < 25}"
                                :style="'width: ' + (percentageToGoal || 0) + '%;'"
                                data-test="percentage-progress-bar"
                            >
                                {{ percentageToGoal }}%
                            </div>
                        </div>
                    </td>
                </tr>
                <tr data-test="revenue-to-date-row">
                    <th><label for="revenueToDate">Online Revenue to Date</label></th>
                    <td data-test="revenue-to-date-value">${{ formatMoney(revenueToDate) }}</td>
                </tr>
                <tr data-test="posted-yield-row">
                    <th><label for="postedPotentialYield">Posted Potential Yield</label></th>
                    <td data-test="posted-yield-value">${{ formatMoney(postedPotentialYield) }}</td>
                </tr>
                <tr data-test="total-yield-row">
                    <th><label for="totalPotentialYield">Total Potential Yield</label></th>
                    <td data-test="total-yield-value">${{ formatMoney(totalPotentialYield) }}</td>
                </tr>
            </table>
        </div>
    </div>
    <div v-else class="revenue-goals">
        <div class="revenue-container">
            <a href="#revenueGoals" @click.prevent="show = !show">
                Online Revenue Goals
                <i class="fal fa-chevron-down"></i>
            </a>
            <div id="revenueGoals">
                <div class="form" v-show="show">
                    <table>
                        <tbody>
                        <tr>
                            <th><label for="seasonGoal">Online Revenue Goal</label></th>
                            <td>
                                <span class="symbol">$</span>
                                <input
                                    type="text"
                                    id="seasonGoal"
                                    v-model.number="revenueGoal"
                                    @input="updateRevenueGoal"
                                />
                            </td>
                        </tr>
                        <tr>
                            <th><label for="percentageToGoal">Percentage to Online Goal</label></th>
                            <td>
                                <span class="symbol right">%</span>
                                {{ Math.round(percentageToGoal || 0) }}
                            </td>
                        </tr>
                        <tr>
                            <th><label for="revenueToDate">Online Revenue to Date</label></th>
                            <td>
                                <span class="symbol">$</span>
                                {{ formatMoney(revenueToDate, false) }}
                            </td>
                        </tr>
                        <tr>
                            <th><label for="postedPotentialYield">Posted Potential Yield</label></th>
                            <td>
                                <span class="symbol">$</span>
                                {{ formatMoney(postedPotentialYield, false) }}
                            </td>
                        </tr>
                        <tr>
                            <th><label for="totalPotentialYield">Total Potential Yield</label></th>
                            <td>
                                <span class="symbol">$</span>
                                {{ formatMoney(totalPotentialYield, false) }}
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'RevenueGoals',

    props: {
        inline: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            show: false,
            loading: false,
            saving: false,
            revenueGoal: 0,
            percentageToGoal: 0,
            totalPotentialYield: 0,
            postedPotentialYield: 0,
            revenueToDate: 0,
        };
    },

    mounted() {
        this.show = this.inline;
        this.loadData();
    },

    methods: {
        async loadData() {
            if (this.loading) return;

            try {
                this.loading = true;

                const response = await axios.get('/api/team/revenue-goals');

                this.revenueGoal = response.data.revenue_goal || 0;
                this.percentageToGoal = response.data.percentage_to_goal || 0;
                this.totalPotentialYield = response.data.total_potential_yield || 0;
                this.postedPotentialYield = response.data.posted_potential_yield || 0;
                this.revenueToDate = response.data.revenue_to_date || 0;
            } catch (error) {
                console.error('Failed to load revenue goals:', error);
            } finally {
                this.loading = false;
            }
        },

        async updateRevenueGoal() {
            if (this.saving) return;

            try {
                this.saving = true;

                const response = await axios.put('/api/team/revenue-goals', {
                    revenue_goal: this.revenueGoal
                });

                // Update all data after the save
                this.revenueGoal = response.data.revenue_goal || 0;
                this.percentageToGoal = response.data.percentage_to_goal || 0;
                this.totalPotentialYield = response.data.total_potential_yield || 0;
                this.postedPotentialYield = response.data.posted_potential_yield || 0;
                this.revenueToDate = response.data.revenue_to_date || 0;
            } catch (error) {
                console.error('Failed to update revenue goal:', error);
                // Reload data on error to revert any changes
                this.loadData();
            } finally {
                this.saving = false;
            }
        },

        formatMoney(amount, showSymbol = true) {
            if (amount === null || amount === undefined) return '0';
            const formatted = Math.round(amount).toLocaleString();
            return showSymbol ? formatted : formatted;
        }
    },

    watch: {
        show(newVal) {
            if (newVal && !this.inline) {
                this.loadData();
            }
        },
    },
};
</script>
